import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { useAuth } from '../App';
import { UserProfile } from '../types';
import { User, Mail, CreditCard, Calendar, MapPin, ShieldCheck, Upload, Save, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion } from 'motion/react';

export default function Profile() {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [uploadingCert, setUploadingCert] = useState(false);

  useEffect(() => {
    if (!authUser) return;
    const fetchProfile = async () => {
      const docRef = doc(db, 'users', authUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setProfile(docSnap.data() as UserProfile);
      }
      setLoading(false);
    };
    fetchProfile();
  }, [authUser]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || !authUser) return;
    setSaving(true);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'users', authUser.uid), {
        name: profile.name,
        cpf: profile.cpf,
        birthDate: profile.birthDate,
        cep: profile.cep,
        specialty: profile.specialty || '',
      });
      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erro ao atualizar perfil.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCertificateUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !authUser) return;

    setUploadingCert(true);
    try {
      const storageRef = ref(storage, `certificates/${authUser.uid}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      await updateDoc(doc(db, 'users', authUser.uid), {
        digitalCertificateUrl: url
      });
      
      setProfile(prev => prev ? { ...prev, digitalCertificateUrl: url } : null);
      setMessage({ type: 'success', text: 'Certificado digital enviado com sucesso!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erro ao enviar certificado.' });
    } finally {
      setUploadingCert(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <header className="mb-12 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-4">
          <User className="w-10 h-10 text-primary" />
        </div>
        <h1 className="text-3xl font-bold text-text-main font-display">Seu Perfil</h1>
        <p className="text-text-muted">Gerencie suas informações pessoais e profissionais.</p>
      </header>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-8 p-4 rounded-lg flex items-center ${
            message.type === 'success' ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-red-50 text-red-800 border border-red-100'
          }`}
        >
          {message.type === 'success' ? <CheckCircle2 className="w-5 h-5 mr-3" /> : <AlertCircle className="w-5 h-5 mr-3" />}
          {message.text}
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <section className="bg-white rounded-2xl shadow-sm border border-text-muted/10 p-8">
            <h2 className="text-xl font-bold text-text-main mb-6 border-b border-text-muted/10 pb-4 font-display">Informações Pessoais</h2>
            <form onSubmit={handleUpdate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-text-main mb-1">Nome Completo</label>
                  <input
                    type="text"
                    value={profile?.name || ''}
                    onChange={(e) => setProfile(prev => prev ? { ...prev, name: e.target.value } : null)}
                    className="w-full border-text-muted/20 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-text-main mb-1">Email</label>
                  <input
                    type="email"
                    value={profile?.email || ''}
                    disabled
                    className="w-full border-text-muted/20 rounded-lg bg-bg-light text-text-muted cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-text-main mb-1">CPF</label>
                  <input
                    type="text"
                    value={profile?.cpf || ''}
                    onChange={(e) => setProfile(prev => prev ? { ...prev, cpf: e.target.value } : null)}
                    className="w-full border-text-muted/20 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-text-main mb-1">Data de Nascimento</label>
                  <input
                    type="date"
                    value={profile?.birthDate || ''}
                    onChange={(e) => setProfile(prev => prev ? { ...prev, birthDate: e.target.value } : null)}
                    className="w-full border-text-muted/20 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-text-main mb-1">CEP</label>
                  <input
                    type="text"
                    value={profile?.cep || ''}
                    onChange={(e) => setProfile(prev => prev ? { ...prev, cep: e.target.value } : null)}
                    className="w-full border-text-muted/20 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
                {profile?.role === 'doctor' && (
                  <div>
                    <label className="block text-sm font-bold text-text-main mb-1">Especialidade</label>
                    <input
                      type="text"
                      value={profile?.specialty || ''}
                      onChange={(e) => setProfile(prev => prev ? { ...prev, specialty: e.target.value } : null)}
                      className="w-full border-text-muted/20 rounded-lg focus:ring-primary focus:border-primary"
                      placeholder="Ex: Cardiologia"
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-6">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-8 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark disabled:opacity-50 shadow-md shadow-primary/20 transition-all"
                >
                  <Save className="w-5 h-5 mr-2" /> {saving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </section>
        </div>

        <div className="space-y-8">
          {/* Role Badge */}
          <section className="bg-white rounded-2xl shadow-sm border border-text-muted/10 p-6 text-center">
            <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Tipo de Conta</p>
            <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide ${
              profile?.role === 'doctor' ? 'bg-blue-100 text-blue-700' :
              profile?.role === 'attendant' ? 'bg-secondary/10 text-secondary-dark' :
              profile?.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-primary/10 text-primary'
            }`}>
              {profile?.role === 'doctor' ? 'Médico' :
               profile?.role === 'attendant' ? 'Atendente' :
               profile?.role === 'admin' ? 'Administrador' : 'Paciente'}
            </span>
          </section>

          {/* Digital Certificate (Doctor Only) */}
          {profile?.role === 'doctor' && (
            <section className="bg-white rounded-2xl shadow-sm border border-text-muted/10 p-6">
              <h3 className="text-lg font-bold text-text-main mb-4 flex items-center font-display">
                <ShieldCheck className="w-5 h-5 mr-2 text-primary" /> Certificado Digital
              </h3>
              <p className="text-sm text-text-muted mb-6">
                O upload do seu certificado digital permite a assinatura eletrônica de prescrições e atestados.
              </p>
              
              {profile.digitalCertificateUrl ? (
                <div className="p-4 bg-primary/10 rounded-xl border border-primary/20 text-center mb-6">
                  <CheckCircle2 className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="text-sm font-bold text-primary">Certificado Ativo</p>
                  <p className="text-xs text-primary/70">Suas prescrições serão assinadas digitalmente.</p>
                </div>
              ) : (
                <div className="p-4 bg-secondary/10 rounded-xl border border-secondary/20 text-center mb-6">
                  <AlertCircle className="w-8 h-8 text-secondary mx-auto mb-2" />
                  <p className="text-sm font-bold text-secondary-dark">Certificado Pendente</p>
                  <p className="text-xs text-secondary-dark/70">Faça o upload para habilitar assinaturas digitais.</p>
                </div>
              )}

              <label className="w-full flex flex-col items-center px-4 py-6 bg-bg-light text-text-muted rounded-xl border-2 border-dashed border-text-muted/30 cursor-pointer hover:bg-bg-light/80 transition-colors">
                <Upload className="w-8 h-8 mb-2" />
                <span className="text-sm font-bold">{uploadingCert ? 'Enviando...' : 'Selecionar Certificado'}</span>
                <input type="file" className="hidden" onChange={handleCertificateUpload} disabled={uploadingCert} />
              </label>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
